import React, { useState } from 'react';
import UISelect from '@/components/shared/UISelect';
import './CalendarWidget.scss';

interface CalendarWidgetProps {
  selectedDate?: Date;
  onDateChange?: (date: Date) => void;
}

const mockHandler = {
  onDateChange: (date: Date) => console.log('Date selected:', date)
};

const CalendarWidget: React.FC<CalendarWidgetProps> = ({
                                                         selectedDate = new Date(),
                                                         onDateChange = mockHandler.onDateChange
                                                       }) => {
  const baseClass = 'CalendarWidget';
  const [currentDate, setCurrentDate] = useState(selectedDate);

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  // Створюємо опції для селектів
  const yearOptions = Array.from({ length: 10 }, (_, i) => {
    const year = currentYear - 5 + i;
    return { value: year, label: year.toString() };
  });

  const monthOptions = months.map((month, index) => ({
    value: index,
    label: month
  }));

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1; // Конвертуємо: неділя=0 -> понеділок=0
  };

  const handleDateClick = (day: number) => {
    const newDate = new Date(currentYear, currentMonth, day);
    setCurrentDate(newDate);
    onDateChange(newDate);
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = parseInt(event.target.value);
    const newDate = new Date(newYear, currentMonth, currentDate.getDate());
    setCurrentDate(newDate);
    onDateChange(newDate);
  };

  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = parseInt(event.target.value);
    const newDate = new Date(currentYear, newMonth, currentDate.getDate());
    setCurrentDate(newDate);
    onDateChange(newDate);
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const days = [];

    // Додаємо заголовки днів тижня
    weekDays.forEach((day, index) => {
      const isWeekend = index >= 5; // SAT, SUN
      days.push(
        <div key={`header-${day}`} className={`${baseClass}__day-header ${isWeekend ? `${baseClass}__day-header--weekend` : ''}`}>
          {day}
        </div>
      );
    });

    // Додаємо порожні клітинки для першого тижня
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className={`${baseClass}__day-cell ${baseClass}__day-cell--empty`}></div>);
    }

    // Додаємо дні місяця
    for (let day = 1; day <= daysInMonth; day++) {
      const dayOfWeek = (firstDay + day - 1) % 7;
      const isWeekend = dayOfWeek >= 5;
      const isSelected = day === currentDate.getDate() &&
        currentMonth === selectedDate.getMonth() &&
        currentYear === selectedDate.getFullYear();
      const isToday = day === 17; // Мок для поточного дня (17 жовтня)

      let cellClass = `${baseClass}__day-cell`;
      if (isWeekend) cellClass += ` ${baseClass}__day-cell--weekend`;
      if (isSelected) cellClass += ` ${baseClass}__day-cell--selected`;
      if (isToday) cellClass += ` ${baseClass}__day-cell--today`;

      days.push(
        <div
          key={day}
          className={cellClass}
          onClick={() => handleDateClick(day)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  return (
    <div className={baseClass}>
      <div className={`${baseClass}__selectors`}>
        <div className={`${baseClass}__year-select`}>
          <UISelect
            options={yearOptions}
            value={currentYear}
            onChange={handleYearChange}
            size="medium"
          />
        </div>

        <div className={`${baseClass}__month-select`}>
          <UISelect
            options={monthOptions}
            value={currentMonth}
            onChange={handleMonthChange}
            size="medium"
          />
        </div>
      </div>

      <div className={`${baseClass}__calendar-grid`}>
        {renderCalendarDays()}
      </div>
    </div>
  );
};

export default CalendarWidget;